import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid, TextField, Switch,
  FormControlLabel, Alert, CircularProgress, Divider, Chip, Tab, Tabs
} from '@mui/material';
import { Save, Settings, CreditCard, Payment } from '@mui/icons-material';
import api from '../../services/api';

export const PaymentSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [configs, setConfigs] = useState<any>({ stripe: null, paypal: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [stripe, setStripe] = useState({ enabled: false, test_mode: true, config: { publishable_key: '', secret_key: '', webhook_secret: '' } });
  const [paypal, setPaypal] = useState({ enabled: false, test_mode: true, config: { client_id: '', client_secret: '' } });

  useEffect(() => {
    api.get('/payments/gateways').then(({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      const s = rows.find((r: any) => r.gateway === 'stripe');
      const p = rows.find((r: any) => r.gateway === 'paypal');
      if (s) setStripe({ enabled: s.enabled, test_mode: s.test_mode, config: { publishable_key: s.config?.publishable_key || '', secret_key: s.config?.secret_key || '', webhook_secret: s.config?.webhook_secret || '' } });
      if (p) setPaypal({ enabled: p.enabled, test_mode: p.test_mode, config: { client_id: p.config?.client_id || '', client_secret: p.config?.client_secret || '' } });
      setConfigs({ stripe: s, paypal: p });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveStripe = async () => {
    setSaving('stripe'); setError(''); setSuccess('');
    try {
      await api.put('/payments/gateways/stripe', stripe);
      setSuccess('Stripe configuration saved.');
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(''); }
  };

  const savePayPal = async () => {
    setSaving('paypal'); setError(''); setSuccess('');
    try {
      await api.put('/payments/gateways/paypal', paypal);
      setSuccess('PayPal configuration saved.');
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(''); }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Settings sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Payment Gateways</Typography>
          <Typography variant="body2" color="text.secondary">Configure Stripe and PayPal to accept payments from customers</Typography>
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<CreditCard />} iconPosition="start" label="Stripe (Cards)" />
        <Tab icon={<Payment />} iconPosition="start" label="PayPal" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Stripe</Typography>
                <Typography variant="body2" color="text.secondary">Accept Visa, Mastercard, Amex and other cards via Stripe</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={stripe.test_mode ? 'TEST MODE' : 'LIVE'} color={stripe.test_mode ? 'warning' : 'success'} size="small" />
                <Chip label={stripe.enabled ? 'Enabled' : 'Disabled'} color={stripe.enabled ? 'success' : 'default'} size="small" />
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel control={<Switch checked={stripe.enabled} onChange={e => setStripe({ ...stripe, enabled: e.target.checked })} />} label="Enable Stripe" />
                <FormControlLabel control={<Switch checked={stripe.test_mode} onChange={e => setStripe({ ...stripe, test_mode: e.target.checked })} />} label="Test Mode (sandbox)" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Publishable Key" value={stripe.config.publishable_key}
                  onChange={e => setStripe({ ...stripe, config: { ...stripe.config, publishable_key: e.target.value } })}
                  placeholder="pk_test_... or pk_live_..."
                  helperText="Safe to expose in frontend — used to initialize Stripe.js" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Secret Key" value={stripe.config.secret_key}
                  onChange={e => setStripe({ ...stripe, config: { ...stripe.config, secret_key: e.target.value } })}
                  placeholder="sk_test_... or sk_live_..." type="password"
                  helperText="Server-side only — never exposed to frontend" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Webhook Secret" value={stripe.config.webhook_secret}
                  onChange={e => setStripe({ ...stripe, config: { ...stripe.config, webhook_secret: e.target.value } })}
                  placeholder="whsec_..." type="password"
                  helperText={`Webhook endpoint: https://esbc2.synergytalk.org/api/payments/stripe/webhook`} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Setup:</strong> Get your keys from <strong>dashboard.stripe.com → Developers → API Keys</strong>. For webhooks, add the endpoint URL above and copy the signing secret.
                </Alert>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={saving === 'stripe' ? <CircularProgress size={16} /> : <Save />}
                onClick={saveStripe} disabled={saving === 'stripe'}>
                Save Stripe Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>PayPal</Typography>
                <Typography variant="body2" color="text.secondary">Accept PayPal payments and PayPal-linked cards</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={paypal.test_mode ? 'SANDBOX' : 'LIVE'} color={paypal.test_mode ? 'warning' : 'success'} size="small" />
                <Chip label={paypal.enabled ? 'Enabled' : 'Disabled'} color={paypal.enabled ? 'success' : 'default'} size="small" />
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel control={<Switch checked={paypal.enabled} onChange={e => setPaypal({ ...paypal, enabled: e.target.checked })} />} label="Enable PayPal" />
                <FormControlLabel control={<Switch checked={paypal.test_mode} onChange={e => setPaypal({ ...paypal, test_mode: e.target.checked })} />} label="Sandbox Mode" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="PayPal Client ID" value={paypal.config.client_id}
                  onChange={e => setPaypal({ ...paypal, config: { ...paypal.config, client_id: e.target.value } })}
                  placeholder="AXxxxxxxxxxx..."
                  helperText="From PayPal Developer Dashboard → My Apps & Credentials" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="PayPal Client Secret" value={paypal.config.client_secret}
                  onChange={e => setPaypal({ ...paypal, config: { ...paypal.config, client_secret: e.target.value } })}
                  placeholder="EXxxxxxxxxxx..." type="password" />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Setup:</strong> Go to <strong>developer.paypal.com → My Apps → Create App</strong>. Use Sandbox for testing, then switch to Live when ready.
                </Alert>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={saving === 'paypal' ? <CircularProgress size={16} /> : <Save />}
                onClick={savePayPal} disabled={saving === 'paypal'}>
                Save PayPal Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
